import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { izniOku, izniKaydet, PANELI_AC } from "@/lib/cerez_izni";

/**
 * Çerez izni paneli (KVKK). İlk ziyarette alt kısımda açılır; seçim yapılana kadar analitik ve pazarlama çerezleri
 * çalışmaz. "Tümünü kabul et" marka renginde birincil, "Reddet" aynı boyutta ve aynı katmanda ikincil düğmedir
 * (izin, reddetmekten daha zor ya da daha kolay olmamalı); kutular hiçbir zaman önceden işaretli gelmez.
 * Footer'daki "Çerez Tercihleri" bağlantısı paneli tercihler görünümünde yeniden açar.
 * Panel açıkken <html data-cerez-paneli> işaretlenir ve panelin yüksekliği --cerez-panel-h değişkenine yazılır;
 * index.css lg altında sabit sohbet (WhatsApp) düğmesini panelin üstüne taşır (gizlemez), böylece panel düğmelerine
 * dokunuş sohbet penceresini açmaz ve paneli yanıtlamayan ziyaretçi de WhatsApp'a ulaşır.
 * Mobilde panel kısa tutulur (ekranın yaklaşık üçte biri) ve header'ın (z-40) altında kalır (z-30): açılır menüdeki
 * "Ücretsiz Analiz Alın", "Randevu" gibi bağlantılar panelin altında kalmaz. Form pencereleri (z-50) panelin üstündedir.
 */
export default function CerezBanner() {
  const [acik, setAcik] = useState(false);
  const [ayrinti, setAyrinti] = useState(false);
  const [analitik, setAnalitik] = useState(false);
  const [pazarlama, setPazarlama] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const izin = izniOku();
    setAnalitik(izin.analitik);
    setPazarlama(izin.pazarlama);
    if (!izin.secildi) setAcik(true);
    const ac = () => {
      const guncel = izniOku();
      setAnalitik(guncel.analitik);
      setPazarlama(guncel.pazarlama);
      setAyrinti(true);
      setAcik(true);
    };
    window.addEventListener(PANELI_AC, ac);
    return () => window.removeEventListener(PANELI_AC, ac);
  }, []);

  useEffect(() => {
    const kok = document.documentElement;
    if (!acik) {
      kok.removeAttribute("data-cerez-paneli");
      kok.style.removeProperty("--cerez-panel-h");
      return;
    }
    kok.setAttribute("data-cerez-paneli", "");
    const panel = panelRef.current;
    const yaz = () => {
      if (panel) kok.style.setProperty("--cerez-panel-h", `${Math.ceil(panel.getBoundingClientRect().height)}px`);
    };
    yaz();
    const izleyici = typeof ResizeObserver !== "undefined" && panel ? new ResizeObserver(yaz) : null;
    if (izleyici && panel) izleyici.observe(panel);
    return () => {
      izleyici?.disconnect();
      kok.removeAttribute("data-cerez-paneli");
      kok.style.removeProperty("--cerez-panel-h");
    };
  }, [acik, ayrinti]);

  if (!acik) return null;

  const kaydet = (a: boolean, p: boolean) => {
    izniKaydet(a, p);
    setAcik(false);
    setAyrinti(false);
  };

  // İki ana düğme birebir aynı ölçüde: aynı dolgu, kenarlık, yazı ve en az yükseklik; her genişlikte yan yana
  const dugme =
    "inline-flex min-h-11 w-full items-center justify-center border-2 border-black px-2 py-2 text-xs tracking-normal sm:min-h-12 sm:px-4 sm:py-3 sm:text-sm sm:tracking-wide font-black uppercase transition-colors focus-visible:outline focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-black";
  const baglanti =
    "font-bold underline decoration-[#9EFF00] decoration-2 underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black";

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cerez-baslik"
      aria-describedby="cerez-metin"
      className="fixed inset-x-0 bottom-0 z-30 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-4 sm:pb-[max(1rem,env(safe-area-inset-bottom))] pointer-events-none"
    >
      <div
        ref={panelRef}
        className="pointer-events-auto mx-auto max-w-3xl max-h-[60vh] overflow-y-auto bg-white border-3 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] sm:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] p-3 sm:p-5 space-y-3 sm:space-y-4"
      >
        <div className="space-y-1 sm:space-y-2">
          <h2 id="cerez-baslik" className="sr-only sm:not-sr-only font-black text-base">Çerez tercihleriniz</h2>
          <p id="cerez-metin" className="text-xs sm:text-sm text-neutral-700 font-medium leading-snug sm:leading-relaxed">
            Analitik çerezler siteyi geliştirmemize, pazarlama çerezleri (Meta ve OpenAI pikselleri) reklamlarımızı
            ölçmemize yardımcı olur. Reddederseniz yalnızca zorunlu çerezler çalışır
            <span className="hidden sm:inline">; seçiminizi sayfa altındaki "Çerez Tercihleri"nden değiştirebilirsiniz</span>{" "}
            (<Link href="/cerez-politikasi" className={baglanti}>Çerez Politikası</Link>).
          </p>
        </div>

        {ayrinti && (
          <fieldset className="space-y-3 border-t-2 border-black pt-4">
            <legend className="sr-only">Çerez kategorileri</legend>
            <div className="flex items-start gap-3 text-sm">
              <input id="cerez-zorunlu" type="checkbox" checked disabled className="mt-1 h-4 w-4 accent-black" />
              <label htmlFor="cerez-zorunlu" className="font-medium text-neutral-700">
                <span className="font-black text-black">Zorunlu</span> — oturum, güvenlik ve bu tercihin saklanması. Kapatılamaz.
              </label>
            </div>
            <div className="flex items-start gap-3 text-sm">
              <input id="cerez-analitik" type="checkbox" checked={analitik} onChange={e => setAnalitik(e.target.checked)} className="mt-1 h-4 w-4 accent-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black" />
              <label htmlFor="cerez-analitik" className="font-medium text-neutral-700">
                <span className="font-black text-black">Analitik</span> — Google Analytics 4 ile ziyaret istatistikleri.
              </label>
            </div>
            <div className="flex items-start gap-3 text-sm">
              <input id="cerez-pazarlama" type="checkbox" checked={pazarlama} onChange={e => setPazarlama(e.target.checked)} className="mt-1 h-4 w-4 accent-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black" />
              <label htmlFor="cerez-pazarlama" className="font-medium text-neutral-700">
                <span className="font-black text-black">Pazarlama</span> — Meta Pixel ve OpenAI (ChatGPT) pikseliyle reklam ölçümü ve dönüşüm takibi.
              </label>
            </div>
          </fieldset>
        )}

        <div className="space-y-2 sm:space-y-3">
          {/* DOM sırası görsel sırayla aynı (klavye odak sırası, WCAG 2.4.3): "Tümünü kabul et" solda, "Reddet"
              sağında, aynı boyutta. CSS order kullanılmaz. */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-cerez="kabul"
              onClick={() => kaydet(true, true)}
              className={`${dugme} bg-[#9EFF00] text-black hover:bg-[#8de600]`}
            >
              Tümünü kabul et
            </button>
            <button
              type="button"
              data-cerez="reddet"
              onClick={() => kaydet(false, false)}
              className={`${dugme} bg-white text-black hover:bg-neutral-100`}
            >
              Reddet
            </button>
          </div>
          {ayrinti ? (
            <button
              type="button"
              onClick={() => kaydet(analitik, pazarlama)}
              className={`${dugme} bg-black text-white hover:bg-neutral-800`}
            >
              Seçimimi kaydet
            </button>
          ) : (
            <div className="flex justify-center sm:justify-start">
              <button type="button" onClick={() => setAyrinti(true)} className={`${baglanti} inline-flex min-h-8 sm:min-h-11 items-center px-1 text-xs sm:text-sm text-black`}>
                Tercihleri yönet
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
